import type { DoorSelection } from "../lib/state/visualizerStore";
import type { PostcardPreset } from "../lib/api/types";
import { dollars, dollarsCents } from "../lib/format";
import { Thumb } from "./Thumb";

// Postcard-tier ribbon labels (owner-locked naming: Best Value / Recommended / Premium; the hero
// is simply "On your postcard"). Shortlist extras (tier null) carry no ribbon.
const TIER_LABELS: Record<string, string> = {
  hero: "On your postcard",
  value: "Best Value",
  recommended: "Recommended",
  premium: "Premium",
};

/**
 * THE preset/selection identity used for the rail's active-card highlight — both sides of the
 * comparison (each card via {@link presetKey}, the applied document state via this function)
 * must build the key here so the formats can never drift apart.
 */
export function selectionKey(
  collectionId: string | null,
  sel: Pick<DoorSelection, "designId" | "colorId" | "windowOptionId" | "hardwareId">,
): string {
  return [collectionId, sel.designId, sel.colorId, sel.windowOptionId, sel.hardwareId].join("|");
}

export function presetKey(p: PostcardPreset): string {
  return selectionKey(p.selection.collectionId, p.selection);
}

/**
 * "Recommended for your home" — the horizontal preset rail of a postcard session (spec §6):
 * the postcard's 4 doors first, then the stored recommendation shortlist. Tapping a card applies
 * that door to EVERY opening (presets span collections, so a per-door apply is meaningless).
 */
export function RecommendedRail({
  salutation,
  presets,
  activeKey,
  onApply,
}: {
  salutation: string;
  presets: PostcardPreset[];
  /** Key of the currently-applied preset (see {@link selectionKey}) — highlights the active card. */
  activeKey: string | null;
  onApply: (preset: PostcardPreset) => void;
}) {
  if (presets.length === 0) return null;
  return (
    <div data-testid="recommended-rail" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Recommended for your home</h3>
        {salutation && <span style={{ color: "var(--muted)", fontSize: 13 }}>{salutation}</span>}
      </div>
      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
        {presets.map((p, i) => {
          const key = presetKey(p);
          const active = key === activeKey;
          const ribbon = p.tier ? TIER_LABELS[p.tier] ?? null : null;
          const price = p.promoCents ?? p.priceCents;
          return (
            <button
              key={`${key}-${i}`}
              type="button"
              className="rg-tile"
              data-testid="rail-preset"
              onClick={() => onApply(p)}
              style={{
                flex: "0 0 auto",
                width: 168,
                padding: 8,
                borderRadius: 10,
                border: `2px solid ${active ? "var(--brand)" : "var(--border-strong)"}`,
                background: active ? "var(--brand-tint)" : "var(--surface)",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              <div style={{ position: "relative" }}>
                {/* Thumb degrades to a clean placeholder when a stored preview URL has gone
                    stale (catalogue re-ingests purge assets) — never the broken-image glyph. */}
                <Thumb src={p.tileUrl} alt={p.displayName} aspect="16 / 9" fit="cover" />
                {ribbon && (
                  <span
                    style={{
                      position: "absolute", top: 6, left: 6, padding: "2px 8px", borderRadius: 999,
                      background: "var(--brand)", color: "var(--on-brand)", fontSize: 10.5, fontWeight: 700,
                    }}
                  >
                    {ribbon}
                  </span>
                )}
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 600, lineHeight: 1.25 }}>{p.displayName}</div>
              {price != null && price > 0 && (
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                  {p.isFrom ? "From " : ""}{dollars(price)}
                  {p.monthlyCents != null && p.monthlyCents > 0 && <> · {dollarsCents(p.monthlyCents)}/mo</>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
